import React, { useState } from 'react';
import { Info, Eye, User, Calendar, Key, CheckCircle } from 'lucide-react';

const PasswordSuggestion: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-lg px-4 py-1 mb-4 transition-all duration-300 hover:shadow-md">
      <div 
        className="flex items-center justify-between cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center space-x-2">
          <div className="p-1.5 bg-blue-100 rounded-full">
            <Info className="w-4 h-4 text-blue-600" />
          </div>
          <span className="text-sm font-medium text-blue-800">
            Password Format Guide
          </span>
        </div>
        <div className={`transform transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}>
          <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
        isExpanded ? 'max-h-96 opacity-100 mt-4' : 'max-h-0 opacity-0'
      }`}>
        <div className="space-y-4">
          {/* Password Format Rules */}
          <div className="bg-white rounded-lg p-4 border border-blue-100">
            <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center">
              <Key className="w-4 h-4 mr-2 text-blue-600" />
              Password Format Rules
            </h4>
            <div className="space-y-2">
              <div className="flex items-start space-x-3">
                <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm text-gray-700">
                    <span className="font-medium">First letter of your first name</span> (Capital case)
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm text-gray-700">
                    <span className="font-medium">Second letter of your first name</span> (Small case)
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm text-gray-700">
                    <span className="font-medium">Your year of birth</span> (4 digits)
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Example */}
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-1 border border-green-200">
            <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center">
              <Eye className="w-4 h-4 mr-2 text-green-600" />
              Example
            </h4>
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-white rounded-lg border border-green-200">
                  <User className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-800">John Doe</p>
                  <p className="text-xs text-gray-600">Full Name</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-white rounded-lg border border-green-200">
                  <Calendar className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-800">2001</p>
                  <p className="text-xs text-gray-600">Year of Birth</p>
                </div>
              </div>
              <div className="border-t border-green-200 pt-3">
                <div className="flex items-center justify-between bg-white rounded-lg p-3 border border-green-200">
                  <div>
                    <p className="text-xs text-gray-600 mb-1">Generated Password:</p>
                    <div className="flex items-center space-x-1">
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm font-mono">J</span>
                      <span className="text-gray-400">+</span>
                      <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-sm font-mono">o</span>
                      <span className="text-gray-400">+</span>
                      <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-sm font-mono">2001</span>
                      <span className="text-gray-400">=</span>
                      <span className="px-3 py-1 bg-gray-800 text-white rounded text-sm font-mono font-bold">Jo2001</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Tips */}
          <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
            <div className="flex items-start space-x-2">
              <div className="p-1 bg-amber-100 rounded-full mt-0.5">
                <svg className="w-3 h-3 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <p className="text-xs text-amber-800">
                <span className="font-medium">Remember:</span> Use the exact format shown above for successful login.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PasswordSuggestion;